import type { ReactNode } from 'react'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { UseSchedulingSmsPolicyReturn } from './useSchedulingSmsPolicy'

export type SchedulingSmsPolicyComponentProps = UseSchedulingSmsPolicyReturn & {
  policy: `terms` | `privacy`
}

function PolicyShell({ locationName, title, children }: { locationName: string; title: string; children: ReactNode }) {
  return (
    <main className="theme-light min-h-dvh bg-surface-muted px-4 py-10 text-default md:py-16">
      <article className="mx-auto max-w-3xl rounded-xl border border-default bg-surface px-6 py-8 shadow-sm md:px-10 md:py-12">
        <p className="text-sm font-medium text-subtle">{locationName}</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-strong">{title}</h1>
        <div className="mt-8 space-y-7 text-base leading-7">{children}</div>
      </article>
    </main>
  )
}

function ContactLine({ phone }: { phone?: string }) {
  const formatted = formatPhoneNumber(phone) || phone
  if (!formatted) return null
  const digits = phone?.replace(/\D/g, ``) || ``
  const dialPhone = digits.length === 10 ? `+1${digits}` : `+${digits}`
  return (
    <p>
      For support, call{' '}
      <a className="font-medium text-blue-700 underline" href={`tel:${dialPhone}`}>
        {formatted}
      </a>
      .
    </p>
  )
}

export function SchedulingSmsPolicyComponent({
  policy,
  location,
  loading,
  unavailable,
}: SchedulingSmsPolicyComponentProps) {
  if (loading) {
    return (
      <main className="theme-light flex min-h-dvh items-center justify-center bg-surface-muted text-subtle">
        Loading…
      </main>
    )
  }

  if (unavailable || !location) {
    return (
      <main className="theme-light flex min-h-dvh items-center justify-center bg-surface-muted px-6 text-center text-subtle">
        SMS policy information is unavailable.
      </main>
    )
  }

  if (policy === `terms`) {
    return (
      <PolicyShell locationName={location.name} title="SMS Terms and Conditions">
        <section>
          <h2 className="text-lg font-semibold text-strong">Program description</h2>
          <p className="mt-2">
            By opting in, you agree to receive text messages from {location.name}. Messages can include appointment
            reminders, scheduling updates, follow-up care information, recall notices, and other patient communications.
          </p>
        </section>
        <section>
          <h2 className="text-lg font-semibold text-strong">Consent and message frequency</h2>
          <p className="mt-2">
            Consent is not a condition of purchase. Message frequency varies based on your appointments and requests.
            Message and data rates may apply.
          </p>
        </section>
        <section>
          <h2 className="text-lg font-semibold text-strong">Opt out and help</h2>
          <p className="mt-2">
            Reply STOP to unsubscribe. Reply HELP for help. After you opt out, you can receive one final confirmation
            message.
          </p>
          <ContactLine phone={location.supportPhone} />
        </section>
        <section>
          <h2 className="text-lg font-semibold text-strong">Carrier terms</h2>
          <p className="mt-2">
            Mobile carriers are not liable for delayed or undelivered messages. Message delivery depends on your device,
            carrier, and network availability.
          </p>
        </section>
      </PolicyShell>
    )
  }

  return (
    <PolicyShell locationName={location.name} title="SMS Privacy Policy">
      <section>
        <h2 className="text-lg font-semibold text-strong">Information we collect</h2>
        <p className="mt-2">
          When you opt in, {location.name} collects your phone number, consent record, and message history to provide
          appointment and patient communication services.
        </p>
      </section>
      <section>
        <h2 className="text-lg font-semibold text-strong">How we use and share SMS information</h2>
        <p className="mt-2">
          We use SMS information to send the messages described in our SMS terms, respond to requests, and maintain
          consent records. We can share information with service providers that deliver these services for us. We do not
          sell SMS consent or phone numbers, and we do not share them with third parties for their marketing purposes.
        </p>
      </section>
      <section>
        <h2 className="text-lg font-semibold text-strong">Your choices</h2>
        <p className="mt-2">
          Reply STOP to end text messages. Reply HELP for help. You can also contact the office to ask about your SMS
          information or consent.
        </p>
        <ContactLine phone={location.supportPhone} />
      </section>
    </PolicyShell>
  )
}
